import React, { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { hasFreeAnonymousHints, getHintAuthMessage } from '../utils/anonymousHints';
import { Link, useNavigate, useLocation } from "react-router-dom";
import { GameShell, useAiutiniModal } from "./layout";
import { GameEndSection, LivesDisplay } from "./game";
import SnackAlert from "./SnackAlert.js";
import { CollegamentiSkeleton } from "./Skeleton";

import { getCurrentDate, getLocalData, setLocalData, hasGameHistory, removeAccents } from "../util";
import { buildTrackedAuthRedirectUrl, logGameEvent, trackGameHintInsufficientBalance } from "../analytics";
import { getGameConfig, getGameMessage } from "../games";
import { useCollegamentiEvents } from "../hooks/games/useCollegamentiEvents";
import { useGettoncini } from "../contexts/GettonciniContext";
import useHasEverAuthenticated from "../hooks/useHasEverAuthenticated";
import AIHelperTrigger from './ai-helper/AIHelperTrigger';
import GettonciniCostChip from './GettonciniCostChip';
import RewardedGettonciniCard from './RewardedGettonciniCard';
import { usePuzzleInstances } from '../contexts/PuzzleInstancesContext';
import styles from "../styles/Collegamenti.module.css";
import modalStyles from "../styles/modal.module.css";
import gameStyles from "../styles/GameStyles.module.css";
import buttonStyles from "../styles/Button.module.css";
import Button from "./Button";
import FaIcon from './FaIcon';
import { getDisabledHintCount, getDisabledHintsMessage } from "../utils/aiutiniModal";

const INSUFFICIENT_HINT_BALANCE_MESSAGE = "Gettoncini insufficienti per questo aiutino.";

const canAffordHintCost = ({ cost, balance, isPreview = false, isFree = false }) => {
  if (isPreview || isFree) return true;
  if (typeof cost !== "number" || typeof balance !== "number") return true;
  return balance >= cost;
};

const CollegamentiGame = ({
  date,
  gameConfig: providedGameConfig,
  puzzleConfig,
  sessionState,
  solutions,
  status,
  gameOver,
  won,
  lost,
  justWon,
  justLost = false,
  isLoading,
  sessionError,
  isEventPending,
  dispatchEvent,
  dispatchHintEvent,
  hintAllowance = null,
  refreshHintAllowance = null,
  isSignedIn,
  isPreview = false,
  previewSponsorship = null,
  showNavigation = true,
  showGameHeader = true,
  showHeaderShareAction = true,
  showStickyAdRail = true,
  showMobileGameplayAd = true,
  simplifyInstructions = false,
  headerActionMode = 'default',
  previewEndLabel,
  showAIHelper = true,
}) => {
  const hasFreeHints = hasFreeAnonymousHints(hintAllowance, 'collegamenti', isSignedIn);
  const navigate = useNavigate();
  const location = useLocation();
  const hasEverAuthenticated = useHasEverAuthenticated();

  const gameConfig = providedGameConfig || getGameConfig("collegamenti");
  const effectiveDate = date || "preview";
  const shouldTrack = !isPreview;

  // Get puzzle instance ID for AI helper
  const { getPuzzleInstanceId, getPuzzleInstance } = usePuzzleInstances();
  const puzzleInstanceId = getPuzzleInstanceId(gameConfig.id, effectiveDate);
  const sponsorship = previewSponsorship || (isPreview ? null : getPuzzleInstance(gameConfig.id, effectiveDate)?.sponsorship || null);
  
  const {
    isModalOpen,
    modalTitle,
    modalContent,
    closeModal,
    showModal,
    showAiutiniModal,
    refreshAiutiniModal,
    isAiutiniModalOpen,
  } = useAiutiniModal();

  useEffect(() => {
    if (isAiutiniModalOpen) refreshHintAllowance?.();
  }, [isAiutiniModalOpen, refreshHintAllowance]);

  // Local UI state
  const [selectedWords, setSelectedWords] = useState([]);
  const [localBoard, setLocalBoard] = useState(null); // For shuffling (UI-only)
  const [revealedAnswers, setRevealedAnswers] = useState([]); // For loss animation and persistence
  const [isAnimatingLoss, setIsAnimatingLoss] = useState(false);
  const [lossRevealComplete, setLossRevealComplete] = useState(false);
  const snackAlertRef = useRef();
  const aiutiniModalSignatureRef = useRef("");
  const gameplayControlsRef = useRef(null);
  
  // Gettoncini context for hint costs display
  const { balance, getHintCost } = useGettoncini();

  // Loading state for hints
  const [loadingHint, setLoadingHint] = useState(null); // 'reveal_theme' | 'reveal_elements' | null
  const [aiutiniError, setAiutiniError] = useState("");
  const [endSectionAnchor, setEndSectionAnchor] = useState(null);
  const isSubmitPending = isEventPending && loadingHint === null;

  // Get Collegamenti-specific event helpers
  const { submitWords, revealTheme, revealElements } = useCollegamentiEvents(dispatchEvent, dispatchHintEvent);

  // Derived state from session
  const lives = sessionState?.lives ?? 5;
  const serverFoundGroups = sessionState?.found_groups ?? [];
  const hintsUsed = sessionState?.hints_used ?? { reveal_theme: false, reveal_elements: false };
  const revealedTheme = sessionState?.revealed_theme ?? null;
  const revealedElements = sessionState?.revealed_elements ?? null;
  
  // Displayed groups: 
  // - Won game: show found_groups (in the order player found them)
  // - Lost game during animation: found_groups + incrementally revealed remaining groups
  // - Lost game on reload: found_groups + remaining groups from solutions
  // - In progress: just the groups player found so far
  const foundGroups = useMemo(() => {
    // Won game: player's found_groups already has all 4 in the order they found them
    if (won) {
      return serverFoundGroups;
    }
    
    // Lost game: show player's found groups first, then remaining unfound groups
    if (lost) {
      const foundThemes = new Set(serverFoundGroups.map(g => g.theme));
      
      // During animation, use incrementally revealed answers
      if (isAnimatingLoss || revealedAnswers.length > 0) {
        const uniqueRevealed = revealedAnswers.filter(g => !foundThemes.has(g.theme));
        return [...serverFoundGroups, ...uniqueRevealed];
      }
      
      // On reload, get remaining groups from solutions (from hook)
      if (solutions && solutions.length > 0) {
        const remainingGroups = solutions.filter(g => !foundThemes.has(g.theme));
        return [...serverFoundGroups, ...remainingGroups];
      }
    }
    
    // Default (in progress or no solutions yet): just the groups player found
    return serverFoundGroups;
  }, [won, lost, isAnimatingLoss, solutions, serverFoundGroups, revealedAnswers]);
  
  // Board: use localBoard if shuffled, otherwise from session state
  const board = useMemo(() => {
    if (localBoard) return localBoard;
    return sessionState?.board ?? [];
  }, [localBoard, sessionState?.board]);

  const ERROR_MESSAGES = {
    INVALID_GROUP: "Questo gruppo non è valido",
    WORD_NOT_IN_BOARD: "Una o più parole non sono presenti nella griglia",
    INVALID_SUBMISSION: "Errore durante l'invio della risposta",
    GAME_LOAD_ERROR: "Errore nel caricamento del gioco",
    SUBMIT_ERROR: "Errore durante l'invio",
    SELECT_4_WORDS: "Seleziona 4 parole",
    GROUP_FOUND: "Gruppo trovato!",
    ALMOST_CORRECT: "Ci sei quasi! Solo una sbagliata!"
  };

  const getErrorMessage = (reason) => {
    switch (reason) {
      case "INVALID_GROUP":
        return ERROR_MESSAGES.INVALID_GROUP;
      case "WORD_NOT_IN_BOARD":
        return ERROR_MESSAGES.WORD_NOT_IN_BOARD;
      case "ALMOST_CORRECT":
        return ERROR_MESSAGES.ALMOST_CORRECT;
      default:
        return ERROR_MESSAGES.INVALID_SUBMISSION;
    }
  };

  // Show instructions modal for first-time players
  useEffect(() => {
    if (isPreview) return;
    if (!isLoading && puzzleConfig && !hasGameHistory(gameConfig.id)) {
      showModal({
        title: "Istruzioni",
        content: <Instructions simplified={simplifyInstructions} onDismiss={closeModal} />
      });
      if (shouldTrack) {
        logGameEvent("instructions_viewed", gameConfig.id, effectiveDate, { first_time: true });
      }
    }
  }, [isLoading, puzzleConfig, effectiveDate, gameConfig.id, showModal, isPreview, shouldTrack]);

  // Auto-select revealed elements when they change
  useEffect(() => {
    if (revealedElements && Array.isArray(revealedElements) && revealedElements.length === 3) {
      setSelectedWords(revealedElements);
    }
  }, [revealedElements]);

  const isWordFound = useCallback((word) => {
    return foundGroups.some(group => 
      group.words.some(foundWord => removeAccents(foundWord).toLowerCase() === removeAccents(word).toLowerCase())
    );
  }, [foundGroups]);

  const getWordLengthClass = (word) => {
    if (word.length <= 6) return "short";
    if (word.length <= 8) return "medium";
    return "long";
  };

  const handleWordClick = (word) => {
    if (gameOver || isAnimatingLoss || isWordFound(word)) return;

    setSelectedWords(prev => {
      if (prev.includes(word)) {
        return prev.filter(w => w !== word);
      }
      if (prev.length >= 4) {
        return prev;
      }
      return [...prev, word];
    });
  };

  const shuffleBoard = () => {
    const availableWords = board.flat().filter(word => !isWordFound(word));
    const shuffled = [...availableWords].sort(() => Math.random() - 0.5);
    const newBoard = [];
    
    for (let i = 0; i < shuffled.length; i += 4) {
      newBoard.push(shuffled.slice(i, i + 4));
    }
    
    setLocalBoard(newBoard);
  };

  // Redirect to auth page for unauthenticated users
  const redirectToAuth = useCallback((allowanceOverride = null) => {
    if (isPreview) return;
    const authUrl = buildTrackedAuthRedirectUrl({
      cta_kind: (hasEverAuthenticated || hintAllowance?.eligible === false) ? 'sign_in' : 'sign_up',
      source_surface: "game_hints",
      gate_reason: "hints_requires_auth",
      next_path: `${location.pathname}${location.search || ''}${location.hash || ''}`,
      message: getHintAuthMessage(allowanceOverride?.game_slug === gameConfig.id ? allowanceOverride : hintAllowance),
      is_returning_device: hasEverAuthenticated,
      game_slug: gameConfig?.id || null,
      track_gate_viewed: true,
      trigger: "hint_click_redirect",
    });
    navigate(authUrl);
  }, [gameConfig?.id, hasEverAuthenticated, location.pathname, location.search, navigate, isPreview, hintAllowance, location.hash]);

  // Get hint costs for display - memoize to avoid re-renders
  const hintCosts = useMemo(() => ({
    reveal_theme: getHintCost("collegamenti", "reveal_theme"),
    reveal_elements: getHintCost("collegamenti", "reveal_elements")
  }), [getHintCost]);

  const hintAffordability = useMemo(() => ({
    reveal_theme: canAffordHintCost({ cost: hintCosts.reveal_theme, balance, isPreview, isFree: hasFreeHints }),
    reveal_elements: canAffordHintCost({ cost: hintCosts.reveal_elements, balance, isPreview, isFree: hasFreeHints }),
  }), [balance, hintCosts.reveal_elements, hintCosts.reveal_theme, isPreview, hasFreeHints]);

  const handleRevealTheme = async () => {
    if (!isSignedIn && !isPreview && !hasFreeHints) {
      redirectToAuth();
      return;
    }
    if (!hintAffordability.reveal_theme) {
      if (shouldTrack) {
        trackGameHintInsufficientBalance({
          gameSlug: gameConfig.id,
          gameDate: effectiveDate,
          hintType: 'hint_reveal_theme',
          source: 'client_precheck',
          balanceBefore: balance,
          hintCost: hintCosts.reveal_theme,
          modalName: 'aiutini',
        });
      }
      setAiutiniError(INSUFFICIENT_HINT_BALANCE_MESSAGE);
      return;
    }
    
    setAiutiniError("");
    setLoadingHint("reveal_theme");
    try {
      const result = await revealTheme(redirectToAuth);
      
      if (!result.success) {
        if (result.error === "INSUFFICIENT_BALANCE") {
          setAiutiniError(INSUFFICIENT_HINT_BALANCE_MESSAGE);
        } else if (result.error === "ALREADY_USED") {
          setAiutiniError("Aiutino gia usato");
        } else if (result.error === "NOT_AUTHENTICATED") {
          return;
        } else if (result.error !== "NOT_AUTHENTICATED") {
          setAiutiniError(result.message || "Errore durante la richiesta");
        }
        return;
      }
      
      // Clear selection when theme hint is used
      setSelectedWords([]);
      snackAlertRef.current?.showSnackAlert("Aiutino usato!", "success", 2000);
      closeModal();
    } catch (e) {
      console.error("Reveal theme hint failed:", e);
      setAiutiniError("Errore durante la richiesta");
    } finally {
      setLoadingHint(null);
    }
  };

  const handleRevealElements = async () => {
    if (!isSignedIn && !isPreview && !hasFreeHints) {
      redirectToAuth();
      return;
    }
    if (!hintAffordability.reveal_elements) {
      if (shouldTrack) {
        trackGameHintInsufficientBalance({
          gameSlug: gameConfig.id,
          gameDate: effectiveDate,
          hintType: 'hint_reveal_elements',
          source: 'client_precheck',
          balanceBefore: balance,
          hintCost: hintCosts.reveal_elements,
          modalName: 'aiutini',
        });
      }
      setAiutiniError(INSUFFICIENT_HINT_BALANCE_MESSAGE);
      return;
    }
    
    setAiutiniError("");
    setLoadingHint("reveal_elements");
    try {
      const result = await revealElements(redirectToAuth);
      
      if (!result.success) {
        if (result.error === "INSUFFICIENT_BALANCE") {
          setAiutiniError(INSUFFICIENT_HINT_BALANCE_MESSAGE);
        } else if (result.error === "ALREADY_USED") {
          setAiutiniError("Aiutino gia usato");
        } else if (result.error === "NOT_AUTHENTICATED") {
          return;
        } else if (result.error !== "NOT_AUTHENTICATED") {
          setAiutiniError(result.message || "Errore durante la richiesta");
        }
        return;
      }
      
      // Words will be auto-selected via useEffect when state updates
      snackAlertRef.current?.showSnackAlert("Aiutino usato!", "success", 2000);
      closeModal();
    } catch (e) {
      console.error("Reveal elements hint failed:", e);
      setAiutiniError("Errore durante la richiesta");
    } finally {
      setLoadingHint(null);
    }
  };

  const handleRevealThemeRef = useRef(handleRevealTheme);
  const handleRevealElementsRef = useRef(handleRevealElements);
  handleRevealThemeRef.current = handleRevealTheme;
  handleRevealElementsRef.current = handleRevealElements;

  // Helper to animate revealing answers on loss
  const animateAnswerReveal = useCallback(async (answers, alreadyFoundThemes) => {
    setIsAnimatingLoss(true);
    setSelectedWords([]);
    
    // Filter to get remaining groups not yet found
    const remainingGroups = answers.filter(
      group => !alreadyFoundThemes.has(group.theme)
    );
    
    // Reveal groups one by one with animation delay
    for (let i = 0; i < remainingGroups.length; i++) {
      await new Promise(resolve => setTimeout(resolve, 1000)); // 1 second delay
      
      setRevealedAnswers(prev => {
        const newRevealed = [...prev, remainingGroups[i]];
        // Persist to localStorage
        const localData = getLocalData(gameConfig.id, effectiveDate, {
        }) || {};
        setLocalData(gameConfig.id, effectiveDate, {
          ...localData,
          revealedAnswers: newRevealed,
        });
        return newRevealed;
      });
    }
    
    // The last group's slide animation lasts 500ms.
    await new Promise(resolve => setTimeout(resolve, 500));
    setIsAnimatingLoss(false);
    setLossRevealComplete(true);
  }, [gameConfig.id, effectiveDate]);

  const handleSubmit = async () => {
    if (selectedWords.length !== 4 || isSubmitPending || isAnimatingLoss) {
      return;
    }

    const result = await submitWords(selectedWords);

    if (!result.success) {
      snackAlertRef.current?.showSnackAlert(ERROR_MESSAGES.SUBMIT_ERROR, "error", 1500);
      return;
    }

    const { valid, reason, theme, answers } = result.result;

    if (valid) {
      // Track correct guess
      if (shouldTrack) {
        logGameEvent("guess_correct", gameConfig.id, effectiveDate);
        logGameEvent("level_completed", gameConfig.id, effectiveDate, {
          level_detail: theme,
          game_progress: (serverFoundGroups.length + 1) / 4
        });
      }

      setSelectedWords([]);
      snackAlertRef.current?.showSnackAlert(ERROR_MESSAGES.GROUP_FOUND, "success", 2000);
    } else {
      // Track incorrect guess
      if (shouldTrack) {
        logGameEvent("guess_incorrect", gameConfig.id, effectiveDate);
      }

      const duration = reason === "ALMOST_CORRECT" ? 3000 : 1500;
      snackAlertRef.current?.showSnackAlert(getErrorMessage(reason), "error", duration);

      // Check if game is now over (lives exhausted)
      const newLives = result.state?.lives ?? lives;
      if (newLives === 0 && answers) {
        snackAlertRef.current?.showSnackAlert("Partita finita", "error", 2000);
        
        // Get themes already found by player
        const alreadyFoundThemes = new Set(serverFoundGroups.map(g => g.theme));
        
        // Animate revealing remaining groups
        animateAnswerReveal(answers, alreadyFoundThemes);
      }
    }
  };

  const getAvailableWords = useCallback(() => {
    return board.flat().filter(word => !isWordFound(word));
  }, [board, isWordFound]);

  const getBoardRows = useCallback(() => {
    const availableWords = getAvailableWords();
    const rows = [];
    const wordsPerRow = 4;
    
    for (let i = 0; i < availableWords.length; i += wordsPerRow) {
      rows.push(availableWords.slice(i, i + wordsPerRow));
    }
    
    // Pad last row if needed
    if (rows.length > 0) {
      const lastRow = rows[rows.length - 1];
      while (lastRow.length < wordsPerRow) {
        lastRow.push(null);
      }
    }
    
    return rows;
  }, [getAvailableWords]);

  // Update Aiutini modal content when loadingHint changes
  useEffect(() => {
    if (!isAiutiniModalOpen) {
      aiutiniModalSignatureRef.current = "";
      setAiutiniError("");
      return;
    }

    const nextSignature = JSON.stringify({
      loadingHint: loadingHint || "",
      aiutiniError,
      revealThemeUsed: !!hintsUsed.reveal_theme,
      revealElementsUsed: !!hintsUsed.reveal_elements,
      disabled: !!gameOver,
      revealThemeCost: hintCosts.reveal_theme ?? null,
      revealElementsCost: hintCosts.reveal_elements ?? null,
      revealThemeAffordable: !!hintAffordability.reveal_theme,
      revealElementsAffordable: !!hintAffordability.reveal_elements,
      isSignedIn,
      hintAllowance,
      hasFreeHints,
      hasEverAuthenticated,
    });

    if (aiutiniModalSignatureRef.current === nextSignature) {
      return;
    }
    aiutiniModalSignatureRef.current = nextSignature;

    refreshAiutiniModal(
      <Aiutini
        revealThemeUsed={hintsUsed.reveal_theme}
        revealElementsUsed={hintsUsed.reveal_elements}
        onRevealTheme={() => handleRevealThemeRef.current?.()}
        onRevealElements={() => handleRevealElementsRef.current?.()}
        disabled={gameOver}
        hintCosts={hintCosts}
        hintAffordability={hintAffordability}
        loadingHint={loadingHint}
        errorMessage={aiutiniError}
        isSignedIn={isSignedIn}
        hasFreeHints={hasFreeHints}
        isPreview={isPreview}
        onAuthRedirect={redirectToAuth}
        hasEverAuthenticated={hasEverAuthenticated || hintAllowance?.eligible === false}
        balance={balance}
      />,
      "Aiutini",
    );
  }, [aiutiniError, gameOver, hasEverAuthenticated, hintAffordability, hintCosts, hintsUsed.reveal_elements, hintsUsed.reveal_theme, isAiutiniModalOpen, isSignedIn, hasFreeHints, loadingHint, redirectToAuth, refreshAiutiniModal, isPreview, hintAllowance]);

  const handleClosePreview = useCallback(() => {
    window.close();
  }, []);

  const showEndSection = gameOver && !isAnimatingLoss && foundGroups.length === 4 && (!justLost || lossRevealComplete);
  const isGameplayAdActive = !gameOver && !isAnimatingLoss;
  const mobileGameplayAd = useMemo(() => ({
    enabled: isGameplayAdActive || !!endSectionAnchor,
    anchors: endSectionAnchor ? [endSectionAnchor] : [gameplayControlsRef],
    placement: `game-shell-${gameConfig.id}-mobile-gameplay-banner`,
    testId: `${gameConfig.id}-mobile-gameplay-banner`,
  }), [endSectionAnchor, gameConfig.id, isGameplayAdActive]);

  // Build header actions
  const fullHeaderActions = [
    {
      icon: "fa fa-question-circle",
      title: "Istruzioni",
      onClick: () => {
        if (shouldTrack) {
          logGameEvent("instructions_viewed", gameConfig.id, getCurrentDate(), { first_time: false });
        }
        showModal({
          title: "Istruzioni",
          content: <Instructions simplified={simplifyInstructions} onDismiss={closeModal} />
        });
      }
    },
    {
      icon: "fa fa-medkit",
      title: "Aiutini",
      onClick: () => {
        setAiutiniError("");
        showAiutiniModal(
          <Aiutini
            revealThemeUsed={hintsUsed.reveal_theme}
            revealElementsUsed={hintsUsed.reveal_elements}
            onRevealTheme={() => handleRevealThemeRef.current?.()}
            onRevealElements={() => handleRevealElementsRef.current?.()}
            disabled={gameOver}
            hintCosts={hintCosts}
            hintAffordability={hintAffordability}
            loadingHint={loadingHint}
            errorMessage=""
            isSignedIn={isSignedIn}
            hasFreeHints={hasFreeHints}
            onAuthRedirect={redirectToAuth}
            hasEverAuthenticated={hasEverAuthenticated || hintAllowance?.eligible === false}
            isPreview={isPreview}
            balance={balance}
          />,
          "Aiutini",
        );
        if (shouldTrack) {
          logGameEvent("help_viewed", gameConfig.id, effectiveDate);
        }
      }
    }
  ];
  const headerActions = headerActionMode === 'instructions-only'
    ? fullHeaderActions.slice(0, 1)
    : fullHeaderActions;

  // Show skeleton while loading
  if (isLoading) {
    return (
      <GameShell
        gameConfig={gameConfig}
        actions={headerActions}
        sponsorship={sponsorship}
        isModalOpen={isModalOpen}
        onModalClose={closeModal}
        modalTitle={modalTitle}
        modalContent={modalContent}
        showNavigation={showNavigation}
        showGameHeader={showGameHeader}
        showHeaderShareAction={showHeaderShareAction}
        showStickyAdRail={showStickyAdRail}
      >
        <div className={styles.container}>
          <p className={gameStyles.greeting}>
            {getGameMessage(gameConfig.id, "not_started")}
          </p>
          <CollegamentiSkeleton />
        </div>
      </GameShell>
    );
  }

  // Show error state
  if (sessionError && !sessionState) {
    return (
      <GameShell
        gameConfig={gameConfig}
        actions={headerActions}
        sponsorship={sponsorship}
        isModalOpen={isModalOpen}
        onModalClose={closeModal}
        modalTitle={modalTitle}
        modalContent={modalContent}
        showNavigation={showNavigation}
        showGameHeader={showGameHeader}
        showHeaderShareAction={showHeaderShareAction}
        showStickyAdRail={showStickyAdRail}
      >
        <div className={styles.container}>
          <p className={gameStyles.greeting}>
            {ERROR_MESSAGES.GAME_LOAD_ERROR}
          </p>
        </div>
      </GameShell>
    );
  }

  const aiSelectionEnabled = gameOver && !isPreview;
  const isAIOnboardingReady =
    showEndSection && !isPreview;

  return (
    <GameShell
      gameConfig={gameConfig}
      actions={headerActions}
      sponsorship={sponsorship}
      isModalOpen={isModalOpen}
      onModalClose={closeModal}
      modalTitle={modalTitle}
      modalContent={modalContent}
      key={sessionState?.session_id || effectiveDate}
      resultsPresentationReady={showEndSection}
      showCelebration={justWon}
      isCompleted={gameOver}
      showNavigation={showNavigation}
      showGameHeader={showGameHeader}
      showHeaderShareAction={showHeaderShareAction}
      showStickyAdRail={showStickyAdRail}
      mobileGameplayAd={showMobileGameplayAd ? mobileGameplayAd : null}
    >
      <SnackAlert ref={snackAlertRef} />
      <div className={styles.container}>
        {!gameOver && (
          <p className={gameStyles.greeting}>
            {getGameMessage(gameConfig.id, "not_started")}
          </p>
        )}

        {/* Found groups display */}
        {gameOver ? (
          showAIHelper ? (
            <AIHelperTrigger
              puzzleInstanceId={puzzleInstanceId}
              gameSlug={gameConfig.id}
              enabled={aiSelectionEnabled}
              onboardingReady={isAIOnboardingReady}
              coachmarkSelector='[data-ai-component="board_word"]'
            >
              <div className={styles.foundGroups}>
                {foundGroups.map((group, index) => (
                  <div
                    key={index}
                    className={styles.groupContainer}
                    style={{ backgroundColor: group.color }}
                  >
                    <div className={styles.groupHeader}><span data-ai-component="group_theme" data-ai-theme={group.theme}>{group.theme}</span></div>
                    <div className={styles.groupWords}>
                      {group.words.map((w, wIdx) => (
                        <React.Fragment key={w}>
                          {wIdx > 0 && ', '}
                          <span data-ai-component="board_word" data-ai-word={w}>{w}</span>
                        </React.Fragment>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </AIHelperTrigger>
          ) : (
            <div className={styles.foundGroups}>
              {foundGroups.map((group, index) => (
                <div
                  key={index}
                  className={styles.groupContainer}
                  style={{ backgroundColor: group.color }}
                >
                  <div className={styles.groupHeader}>{group.theme}</div>
                  <div className={styles.groupWords}>{group.words.join(", ")}</div>
                </div>
              ))}
            </div>
          )
        ) : (
          <div className={styles.foundGroups}>
            {foundGroups.map((group, index) => (
              <div 
                key={index} 
                className={styles.groupContainer} 
                style={{ backgroundColor: group.color }}
              >
                <div className={styles.groupHeader}>{group.theme}</div>
                <div className={styles.groupWords}>{group.words.join(", ")}</div>
              </div>
            ))}
          </div>
        )}

        {/* Game board */}
        <div className={styles.gameBoard}>
          {getBoardRows().map((row, rowIndex) => (
            <div key={rowIndex} className={styles.boardRow}>
              {row.map((word, wordIndex) => (
                <div
                  key={wordIndex}
                  className={`${styles.wordTile} ${!word ? styles.empty : ""} ${selectedWords.includes(word) ? styles.selected : ""} ${isWordFound(word) ? styles.found : ""}`}
                  data-length={word ? getWordLengthClass(word) : ""}
                  onClick={() => word && handleWordClick(word)}
                >
                  {word && (
                    <div className={styles.wordTileContent}>
                      {word}
                    </div>
                  )}
                </div>
              ))}
            </div>
          ))}
        </div>

        {/* Hint display area - below the board */}
        {(revealedTheme || revealedElements) && !gameOver && (
          <div className={styles.hintDisplay}>
            {revealedTheme && (
              <div className={styles.hintItem}>
                <FaIcon className="fa-solid fa-tag"  />
                <span><strong>{revealedTheme}</strong></span>
              </div>
            )}
            {revealedElements && (
              <div className={styles.hintItem}>
                <FaIcon className="fa-solid fa-layer-group"  />
                <span><strong>{revealedElements.join(", ")}, ?</strong></span>
              </div>
            )}
          </div>
        )}

        {/* Lives display - only during gameplay */}
        {!gameOver && (
          <LivesDisplay lives={lives} maxLives={5} />
        )}

        {/* Game end section - shows after all groups are revealed */}
        {showEndSection && (
          isPreview && !previewSponsorship ? (
            <div ref={setEndSectionAnchor} className={styles.previewEndSection}>
              <Button primary onClick={handleClosePreview}>
                {previewEndLabel || "Chiudi anteprima"}
              </Button>
            </div>
          ) : (
            <GameEndSection
              ref={setEndSectionAnchor}
              won={won}
              justWon={justWon}
              gameConfig={gameConfig}
              sessionState={sessionState}
              puzzleConfig={puzzleConfig}
              justCompleted={justWon || justLost}
              date={effectiveDate}
              shouldTrack={shouldTrack}
              sponsorship={sponsorship}
              previewMode={isPreview}
              message={won ? "Hai trovato tutti i gruppi! 🚀" : "Hai esaurito tutte le vite! 💔"}
              snackAlertRef={snackAlertRef}
            />
          )
        )}

        {/* Game controls - only during gameplay */}
        {!gameOver && !isAnimatingLoss && (
          <div className={styles.selectionControls} ref={gameplayControlsRef}>
            <Button onClick={shuffleBoard} disabled={gameOver || isAnimatingLoss}>
              Mischia
            </Button>
            <Button onClick={() => {
              setSelectedWords([]);
            }} disabled={selectedWords.length === 0 || gameOver || isAnimatingLoss}>
              Annulla
            </Button>
            <Button 
              primary 
              className={styles.submit} 
              onClick={handleSubmit} 
              disabled={selectedWords.length !== 4 || gameOver || isSubmitPending || isAnimatingLoss}
              loading={isSubmitPending}
              loadingText="Invio"
            >
              Invia
            </Button>
          </div>
        )}
      </div>
    </GameShell>
  );
};

export default CollegamentiGame;

const MiniGridAnimation = () => {
  // 1-based positions: (1,3), (2,1), (3,3), (4,2)
  // 0-based indices: (0,2), (1,0), (2,2), (3,1)
  const highlightOrder = [2, 4, 10, 13]; // 0-based flat indices for 4x4 grid
  const [active, setActive] = useState(0);
  useEffect(() => {
    let timeout;
    if (active < highlightOrder.length) {
      timeout = setTimeout(() => setActive(active + 1), 400);
    } else {
      timeout = setTimeout(() => setActive(0), 700);
    }
    return () => clearTimeout(timeout);
  }, [active]);
  return (
    <div className="mini-grid-animation-grid">
      {[...Array(4)].map((_, row) => (
        <div className="mini-grid-row" key={row}>
          {[...Array(4)].map((_, col) => {
            const idx = row * 4 + col;
            const isActive = highlightOrder.slice(0, active).includes(idx);
            return (
              <div
                key={col}
                className={`mini-grid-block${isActive ? " active" : ""}`}
              />
            );
          })}
        </div>
      ))}
    </div>
  );
};

const Instructions = ({ simplified = false, onDismiss = null }) => {
  if (simplified) return (
    <div className={`${modalStyles.modalContent} ${modalStyles.instructions}`}>
      <p>Trova 4 gruppi di 4 parole che condividono un tema o un aspetto linguistico comune.</p>
      <div className={styles.instructionsGraphicBlock}>
        <MiniGridAnimation />
      </div>
      <p>Ecco un esempio di un gruppo facile:</p>
      <div
        className={`${styles.groupContainer} ${styles.instructionsExampleCard}`}
        style={{ backgroundColor: "#f4cf22" }}
      >
        <div className={styles.groupHeader}>Colori dei capelli</div>
        <div className={styles.groupWords}>BIONDO, CASTANO, NERO, ROSSO</div>
      </div>
      <p>E un altro di un gruppo più difficile:</p>
      <div
        className={`${styles.groupContainer} ${styles.instructionsExampleCard}`}
        style={{ backgroundColor: "#b76dc5" }}
      >
        <div className={styles.groupHeader}>Fare ________</div>
        <div className={styles.groupWords}>PACE, LA SPESA, COLAZIONE, FINTA</div>
      </div>
      <Button
        primary
        onClick={onDismiss}
        className={`${buttonStyles.primaryBlue} ${buttonStyles.inline}`}
      >
        Gioca
        <FaIcon className={`fa-solid fa-play ${styles.instructionsCtaIcon}`} />
      </Button>
    </div>
  );
  return (
    <div className={`${modalStyles.modalContent} ${modalStyles.instructions}`}>
      <p>Trova 4 gruppi di 4 parole che condividono un tema o un aspetto linguistico comune.</p>
      <MiniGridAnimation />
      <ul>
        <li>Seleziona 4 parole e clicca "Invia" per verificare il gruppo.</li>
        <li>Le parole possono avere un collegamento tematico o linguistico.</li>
        <li>Hai a disposizione 5 vite: ogni errore ne consuma una.</li>
      </ul>
      <p>Ecco un esempio di un gruppo facile:</p>
      <div className={styles.groupContainer} style={{ backgroundColor: "#f4cf22" }}>
        <div className={styles.groupHeader}>Colori dei capelli</div>
        <div className={styles.groupWords}>BIONDO, CASTANO, NERO, ROSSO</div>
      </div>
      <p>E un altro di un gruppo più difficile:</p>
      <div className={styles.groupContainer} style={{ backgroundColor: "#b76dc5" }}>
        <div className={styles.groupHeader}>Fare ________</div>
        <div className={styles.groupWords}>PACE, LA SPESA, COLAZIONE, FINTA</div>
      </div>
      <p>Se resti bloccato, puoi usare uno degli aiutini <FaIcon className="fa fa-medkit"  /> a tua disposizione in alto a destra.</p>
    </div>
  );
};

const Aiutini = ({ 
  revealThemeUsed, 
  revealElementsUsed, 
  onRevealTheme, 
  onRevealElements, 
  disabled, 
  hintCosts = {},
  hintAffordability = {},
  loadingHint = null,
  errorMessage = "",
  isSignedIn = true,
  hasFreeHints = false,
  onAuthRedirect = null,
  hasEverAuthenticated = false,
  isPreview = false,
  balance = 0,
}) => {
  // Helper to render button content with cost
  const renderButtonContent = ({ alreadyUsed = false } = {}) => {
    if (isPreview) {
      return "Ricevi aiutino";
    }
    if (!isSignedIn && !hasFreeHints) {
      return hasEverAuthenticated ? 'Accedi' : 'Registrati';
    }
    if (alreadyUsed) {
      return "Già usato";
    }
    return "Usa aiutino";
  };
  const shouldShowCostChip = (cost) => !isPreview && (hasFreeHints || (Number.isFinite(Number(cost)) && Number(cost) > 0));
  const disabledHintCount = getDisabledHintCount({
    isSignedIn,
    isPreview,
    hintAffordability,
    excludedHintKeys: [
      ...(revealThemeUsed ? ['reveal_theme'] : []),
      ...(revealElementsUsed ? ['reveal_elements'] : []),
    ],
  });
  const shouldShowInsufficientBalanceState = isSignedIn && (
    errorMessage === INSUFFICIENT_HINT_BALANCE_MESSAGE
    || disabledHintCount > 0
  );
  const shouldShowGenericError = Boolean(errorMessage) && !shouldShowInsufficientBalanceState;

  return (
    <div className={modalStyles.modalContent}>
      {shouldShowInsufficientBalanceState && (
        <div className={styles.aiutiniInlineError} role="alert">
          <FaIcon className="fa-solid fa-circle-exclamation" />
          <div className={styles.aiutiniInlineErrorContent}>
            <div>
              {getDisabledHintsMessage({
                balance,
                disabledHintCount: Math.max(disabledHintCount, 1),
              })}
            </div>
            <div className={styles.aiutiniInlineErrorCard}>
              <RewardedGettonciniCard
                sourceSurface="aiutini_modal"
                gameSlug="collegamenti"
                compact
              />
            </div>
          </div>
        </div>
      )}
      {shouldShowGenericError && (
        <div className={styles.aiutiniInlineError} role="alert">
          <FaIcon className="fa-solid fa-circle-exclamation" />
          <span>
            {errorMessage}
          </span>
        </div>
      )}
      {(!isSignedIn || !shouldShowInsufficientBalanceState) && (
        <p className={styles.aiutinoPreamble}>
          {!isSignedIn && !isPreview && !hasFreeHints ? (
            <>
              Per usare gli aiutini hai bisogno di gettoncini, che puoi guadagnare con un account gratuito.{" "}
              <span className={styles.authLink} onClick={onAuthRedirect}>
                {hasEverAuthenticated ? "Accedi" : "Registrati"} <FaIcon className="fa-solid fa-arrow-right"  />
              </span>
            </>
          ) : "Se sei bloccato, puoi usare uno di questi aiutini. Ogni aiutino può essere usato una sola volta."}
        </p>
      )}
      <div className={styles.aiutinoRow}>
        <div className={styles.aiutinoHeaderRow}>
          <div className={styles.aiutinoHeaderTitle}>
            <span className={styles.aiutinoIcon}><FaIcon className="fa-solid fa-tag"  /></span>
            <span className={styles.aiutinoTitle}>Rivela tema</span>
          </div>
          <div className={styles.aiutinoActionWrap}>
            <Button
              secondary
              onClick={onRevealTheme}
              disabled={disabled || revealThemeUsed || loadingHint !== null || (isSignedIn && !revealThemeUsed && !hintAffordability.reveal_theme)}
              className={[
                styles.aiutinoActionRight,
                isSignedIn && !revealThemeUsed && !hintAffordability.reveal_theme ? styles.aiutinoUnavailable : "",
              ].filter(Boolean).join(" ")}
              loading={loadingHint === "reveal_theme"}
              aria-disabled={isSignedIn && !revealThemeUsed && !hintAffordability.reveal_theme}
            >
              {renderButtonContent({ alreadyUsed: revealThemeUsed })}
            </Button>
            {shouldShowCostChip(hintCosts.reveal_theme) && (
              <GettonciniCostChip free={hasFreeHints} amount={hintCosts.reveal_theme} compact className={styles.aiutinoCostChip} />
            )}
          </div>
        </div>
        <span className={styles.aiutinoDesc}>Mostra il tema di uno dei gruppi non ancora scoperti.</span>
      </div>

      <div className={styles.aiutinoRow}>
        <div className={styles.aiutinoHeaderRow}>
          <div className={styles.aiutinoHeaderTitle}>
            <span className={styles.aiutinoIcon}><FaIcon className="fa-solid fa-layer-group"  /></span>
            <span className={styles.aiutinoTitle}>Rivela elementi</span>
          </div>
          <div className={styles.aiutinoActionWrap}>
            <Button
              secondary
              onClick={onRevealElements}
              disabled={disabled || revealElementsUsed || loadingHint !== null || (isSignedIn && !revealElementsUsed && !hintAffordability.reveal_elements)}
              className={[
                styles.aiutinoActionRight,
                isSignedIn && !revealElementsUsed && !hintAffordability.reveal_elements ? styles.aiutinoUnavailable : "",
              ].filter(Boolean).join(" ")}
              loading={loadingHint === "reveal_elements"}
              aria-disabled={isSignedIn && !revealElementsUsed && !hintAffordability.reveal_elements}
            >
              {renderButtonContent({ alreadyUsed: revealElementsUsed })}
            </Button>
            {shouldShowCostChip(hintCosts.reveal_elements) && (
              <GettonciniCostChip free={hasFreeHints} amount={hintCosts.reveal_elements} compact className={styles.aiutinoCostChip} />
            )}
          </div>
        </div>
        <span className={styles.aiutinoDesc}>Mostra 3 dei 4 elementi di un gruppo non ancora scoperto.</span>
      </div>
    </div>
  );
};
